import React from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Container, Paper, Typography, Box, Button, Alert } from '@mui/material';
import { useNavigate } from 'react-router-dom';

const AuthStatus: React.FC = () => {
  const { 
    user, 
    token, 
    isAuthenticated, 
    loading, 
    isAdmin, 
    hasRole,
    hasAnyRole
  } = useAuth();

  const navigate = useNavigate();

  const storedToken = localStorage.getItem('auth_token');
  const storedUser = localStorage.getItem('user_data');
  const parsedUser = storedUser ? JSON.parse(storedUser) : null;

  const testAdminAccess = () => {
    const adminCheck = hasAnyRole(['admin']);
    const roleCheck = hasRole('admin');
    const isAdminCheck = isAdmin();
    
    alert(`Admin Access Tests:
    - hasAnyRole(['admin']): ${adminCheck}
    - hasRole('admin'): ${roleCheck}  
    - isAdmin(): ${isAdminCheck}
    - User role: ${user?.role}
    - User object: ${JSON.stringify(user, null, 2)}`);
  };

  const clearAuth = () => {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user_data');
    window.location.reload();
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Paper sx={{ p: 4 }}>
        <Typography variant="h4" gutterBottom>
          🔍 Authentication Status Debug
        </Typography>

        <Box sx={{ my: 3 }}>
          <Alert severity={isAuthenticated ? "success" : "error"}>
            Authentication Status: {isAuthenticated ? "✅ Authenticated" : "❌ Not Authenticated"}
          </Alert>
        </Box>

        <Box sx={{ my: 3 }}>
          <Typography variant="h6">Context State:</Typography>
          <Typography>• Loading: {loading ? "Yes" : "No"}</Typography>
          <Typography>• Token Present: {token ? "Yes" : "No"}</Typography>
          <Typography>• User Present: {user ? "Yes" : "No"}</Typography>
        </Box>

        {user && (
          <Box sx={{ my: 3 }}>
            <Typography variant="h6">User from Context:</Typography>
            <Typography>• ID: {user.id}</Typography>
            <Typography>• Name: {user.name}</Typography>
            <Typography>• Email: {user.email}</Typography>
            <Typography>• Role: <strong>{user.role}</strong></Typography>
            <Typography>• Active: {user.is_active ? "Yes" : "No"}</Typography>
          </Box>
        )}

        <Box sx={{ my: 3 }}>
          <Typography variant="h6">LocalStorage:</Typography>
          <Typography>• Token stored: {storedToken ? "Yes" : "No"}</Typography>
          <Typography>• User data stored: {storedUser ? "Yes" : "No"}</Typography>
          {parsedUser && (
            <Box sx={{ mt: 1 }}>
              <Typography>• Stored user role: <strong>{parsedUser.role}</strong></Typography>
              <Typography>• Stored user email: {parsedUser.email}</Typography>
            </Box>
          )}
        </Box>

        <Box sx={{ my: 3 }}>
          <Typography variant="h6">Role Check Results:</Typography>
          <Typography>• isAdmin(): {isAdmin() ? "✅ True" : "❌ False"}</Typography>
          <Typography>• hasRole('admin'): {hasRole('admin') ? "✅ True" : "❌ False"}</Typography>
          <Typography>• hasAnyRole(['admin']): {hasAnyRole(['admin']) ? "✅ True" : "❌ False"}</Typography>
          <Typography>• hasAnyRole(['admin', 'developer']): {hasAnyRole(['admin', 'developer']) ? "✅ True" : "❌ False"}</Typography>
        </Box>

        <Box sx={{ my: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Button variant="contained" onClick={testAdminAccess}>
            Test Admin Access
          </Button>
          <Button variant="outlined" onClick={() => navigate('/quick-login')}>
            Go to Login
          </Button>
          <Button variant="outlined" onClick={() => navigate('/dashboard/admin')}>
            Try Admin Dashboard
          </Button>
          <Button variant="outlined" color="error" onClick={clearAuth}>
            Clear Auth Data
          </Button>
        </Box>

        {storedToken && (
          <Box sx={{ my: 3 }}>
            <Typography variant="h6">Token (first 100 chars):</Typography>
            <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
              {storedToken.substring(0, 100)}...
            </Typography>
          </Box>
        )}

        {storedUser && (
          <Box sx={{ my: 3 }}>
            <Typography variant="h6">Raw User Data:</Typography>
            <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
              {storedUser}
            </Typography>
          </Box>
        )}
      </Paper>
    </Container>
  );
};

export default AuthStatus;